/**
 * UpdateProgressCell — celda de progreso reutilizable para mostrar el estado
 * de actualización de un agente. Usada en UpdatesPage (fila de la tabla) y
 * en AgentDetail (drawer de detalle).
 *
 * Por qué existe: el `percent` que reporta el agente puede bajar a 0
 * durante un `retrying` y oscilar entre fases. Para que la barra no
 * "parpadee" y para mostrar mensajes pacientes durante reinicios del agente,
 * la lógica vive en `lib/updates/useSmoothUpdateState`.
 *
 * Variantes:
 *   - `compact` (default en UpdatesPage): label + barra + percent
 *   - `detailed` (AgentDetail): label + hint + barra
 */

import { Button, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import {
  CheckCircle as UpToDateIcon,
  ErrorOutline as ErrorIcon,
  Loop as RetryIcon,
} from '@mui/icons-material';
import type { Agent, AgentUpdateState } from '@/types/agent';
import { useSmoothUpdateState } from '@/lib/updates/useSmoothUpdateState';

export interface UpdateProgressCellProps {
  state: AgentUpdateState | null;
  agent: Pick<Agent, 'agentId' | 'connected' | 'version'>;
  /** Versión "latest" del manifiesto. Si se pasa, permite detectar
   *  "complete perdido" cuando el agente vuelve con la versión nueva. */
  latestVersion?: string;
  /** Click handler del botón "Reintentar" (cuando hay error). */
  onRetry?: () => void;
  /** Layout denso (default true) para tablas; false para drawer de detalle. */
  compact?: boolean;
  /** Si se provee, deshabilita el botón de reintento cuando el agente
   *  está offline. Default: !agent.connected. */
  retryDisabled?: boolean;
}

export const UpdateProgressCell: React.FC<UpdateProgressCellProps> = ({
  state,
  agent,
  latestVersion,
  onRetry,
  compact = true,
  retryDisabled,
}) => {
  const display = useSmoothUpdateState({ state, agent, latestVersion });

  if (!state || !display) return null;

  if (display.phase === 'error') {
    return (
      <Stack spacing={0.5} sx={compact ? undefined : { width: '100%' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ErrorIcon fontSize="small" color="error" />
          <Typography variant="caption" color="error.main">
            {display.error || 'Error desconocido'}
          </Typography>
        </Stack>
        {display.retryCount !== undefined && display.retryCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            Intentos: {display.retryCount}
          </Typography>
        )}
        {onRetry && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<RetryIcon />}
            onClick={onRetry}
            disabled={retryDisabled ?? !agent.connected}
            fullWidth={!compact}
          >
            Reintentar
          </Button>
        )}
      </Stack>
    );
  }

  if (display.phase === 'complete' || display.phase === 'already-up-to-date') {
    return (
      <Chip
        icon={<UpToDateIcon fontSize="small" />}
        label={display.label}
        color="success"
        size="small"
      />
    );
  }

  const showPercent = display.determinate && display.percent > 0;
  return (
    <Stack
      spacing={0.5}
      sx={{ minWidth: compact ? 140 : undefined, width: compact ? undefined : '100%' }}
    >
      <Typography variant="caption" color="text.secondary">
        {display.label}
      </Typography>
      <LinearProgress
        variant={display.determinate ? 'determinate' : 'indeterminate'}
        value={display.percent}
        sx={{ borderRadius: 1, minHeight: 4 }}
      />
      {compact && showPercent && (
        <Typography variant="caption" color="text.secondary" align="right">
          {Math.round(display.percent)}%
        </Typography>
      )}
      {!compact && display.hint && (
        <Typography variant="caption" color="text.secondary">
          {display.hint}
        </Typography>
      )}
    </Stack>
  );
};
